// JWT Token utilities
export interface UserProfile {
  id: string;
  email: string;
  name: string;
  profilePicture?: string; // Google profile picture URL
  iat: number; // issued at
  exp: number; // expires at
}

/**
 * Decode JWT token to extract user profile
 * @param token - JWT token string
 * @returns UserProfile object or null if invalid
 */
export function decodeJWTToken(token: string): UserProfile | null {
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }

    // Decode the payload (middle part)
    const payload = parts[1];
    
    // Add padding if needed (base64 requires length to be multiple of 4)
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    
    // Decode base64
    const decodedPayload = atob(paddedPayload);
    
    // Parse JSON
    const userProfile: UserProfile = JSON.parse(decodedPayload);
    
    // Validate required fields
    if (!userProfile.id || !userProfile.email || !userProfile.name) {
      console.error('Missing required fields in token payload');
      return null;
    }
    return userProfile;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
}

/**
 * Check if JWT token is expired
 * @param token - JWT token string
 * @returns boolean - true if expired
 */
export function isTokenExpired(token: string): boolean {
  const profile = decodeJWTToken(token);
  if (!profile) return true;

  const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
  return currentTime > profile.exp;
}

/**
 * Get user profile from stored token
 * @returns UserProfile object or null
 */
export function getCurrentUserProfile(): UserProfile | null {
  const token = localStorage.getItem('authToken');
  if (!token) return null;

  if (isTokenExpired(token)) {
    // Clean up expired token
    localStorage.removeItem('authToken');
    return null;
  }

  // Normalize shape so components can read either `profilePicture` or `avatar`
  const decoded = decodeJWTToken(token);
  if (!decoded) return null;
  const normalized: any = { ...decoded };
  // ensure both keys exist
  if (decoded.profilePicture && !decoded.avatar) normalized.avatar = decoded.profilePicture;
  if (decoded.avatar && !decoded.profilePicture) normalized.profilePicture = decoded.avatar;
  return normalized as UserProfile;
}

/**
 * Format the profile for display
 * @param profile - UserProfile object
 * @returns formatted profile information
 */
export function formatUserProfile(profile: UserProfile) {
  return {
    id: profile.id,
    email: profile.email,
    name: profile.name,
    profilePicture: profile.profilePicture || '',
    initials: profile.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2),
    issuedAt: new Date(profile.iat * 1000).toLocaleString(),
    expiresAt: new Date(profile.exp * 1000).toLocaleString(),
    timeUntilExpiry: Math.max(0, profile.exp - Math.floor(Date.now() / 1000))
  };
}

// src/utils/auth.js
export function getUserIdFromToken() {
  const token = localStorage.getItem("authToken"); // use the same storage key as the rest of the app
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userId || payload._id || payload.id || payload.sub;
  } catch {
    return null;
  }
}

/**
 * Get the raw auth token string from localStorage.
 * Used for SSE connections (EventSource can't set Authorization headers)
 * so the token is passed as ?token= query param instead.
 */
export function getAuthToken(): string | null {
  return localStorage.getItem('authToken');
}
